import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { AppService } from 'src/app/core/services/app.service';
import { CommanSubscribeService } from 'src/app/core/services/comman-subscribe.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-add-channel-community-dialog',
  templateUrl: './add-channel-community-dialog.component.html',
  styleUrls: ['./add-channel-community-dialog.component.scss']
})
export class AddChannelCommunityDialogComponent implements OnInit, OnDestroy {
  communityForm: FormGroup;
  pollForm: FormGroup;
  userInfo: any;
  userInfoSubscription: Subscription;
  organizationId: any;
  searchResults: any[] = [];
  teamsList: any[] = [];
  settingsData: any = {};
  settingsDataSubscription: Subscription;

  constructor(
    private _formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AddChannelCommunityDialogComponent>,
    private toastr: ToastrService,
    private appTvService: AppService,
    private commanSubscribeService: CommanSubscribeService,

    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.settingsData = this.commanSubscribeService.icWebpageSettingsData;
    this.addStyle();
  }

  ngOnInit(): void {
    this.userInfo = this.commanSubscribeService.userInfo;
    this.organizationId = this.userInfo?.organization_id;

    this.userInfoSubscription = this.commanSubscribeService.getUserInfo().subscribe((response) => {
      this.userInfo = response;
      this.organizationId = this.userInfo.organization_id;
    });

    // Initialize appropriate form
    if (this.data.isAddChannel) {
      this.communityForm = this._formBuilder.group({
        channel_name: ['', [Validators.required]],
        description: ['', [Validators.required]],
      });
    } else if (this.data.isReportUser || this.data.isReportChannel) {
      this.communityForm = this._formBuilder.group({
        reason: ['', [Validators.required]],
        description: ['', [Validators.required]],
      });
    } else if (this.data.isPoll) {
      this.pollForm = this._formBuilder.group({
        question: ['', [Validators.required]],
        options: this._formBuilder.array([
          this._formBuilder.control('', Validators.required),
          this._formBuilder.control('', Validators.required)
        ]),
        allowMultiple: [false]
      });
    }

    // Theme settings
    this.settingsDataSubscription = this.commanSubscribeService.getICWebpageThemeSettings().subscribe((response) => {
      this.settingsData = response;
      this.addStyle();
    });

  }

  // ✅ Properly reference pollForm here
  get options(): FormArray {
    return this.pollForm.get('options') as FormArray;
  }

  addOption(): void {
    this.options.push(this._formBuilder.control('', Validators.required));
  }

  removeOption(index: number): void {
    if (this.options.length > 2) {
      this.options.removeAt(index);
    }
  }

  createPoll(): void {
    if (this.pollForm.valid) {
      const pollData = this.pollForm.value;
      const userId = this.userInfo?.user_first_name + ' ' + this.userInfo?.user_last_name;
      (window as any).umami?.track(`Poll is created - ${userId}`)
      const payload = {
        actionType: "community",
        metaData: {
          title: `Poll is created`,
          timeStamp: new Date().toISOString()
        },
        organizationId: this.userInfo?.organizationId || null,

        clickAction: 'create-poll'
      }
      this.commanSubscribeService.sendTriggeredEvent(payload);

      // Submit or emit pollData as needed
      this.dialogRef.close(pollData);
    } else {
      this.toastr.error('Please fill out the required fields.');
    }
  }

  closeDialog(returnData: boolean, isReport?: { condition?: boolean }) {
    
    let item = "Space";
    if (arguments.length < 2) {
      item = "Poll"; // not passed at all
    } else if (isReport?.condition === true) {
      item = "Report channel";
    }

    // STATUS
    let status: string;
    if (arguments.length < 2) {
      status = "Cancelled"; // not passed at all
    } else if (returnData && isReport?.condition === true) {
      status = "Reported";
    } else if (returnData && isReport?.condition === undefined) {
      status = "Created";
    } else {
      status = "Cancelled"; // fallback for false
    }

    const userId = `${this.userInfo?.user_first_name} ${this.userInfo?.user_last_name}`;
    (window as any).umami?.track(`${item} is ${status} - ${userId}`);

    const payload = {
      actionType: "community",
      metaData: {
        title: `${item} is ${status}`,
        timeStamp: new Date().toISOString()
      },
      organizationId: this.userInfo?.organizationId || null,
      clickAction: "space-saved"
    };
    this.commanSubscribeService.sendTriggeredEvent(payload);

    if (this.data.isAddChannel && returnData && this.communityForm.valid) {
      this.dialogRef.close(this.communityForm.value);
    }
    else if ((this.data.isReportChannel && returnData && this.communityForm.valid) || (this.data.isReportUser && returnData && this.communityForm.valid)) {
      this.dialogRef.close(this.communityForm.value);
    }
    else if (!returnData) {
      this.dialogRef.close(null);
    }
  }

  onTeamSelected(team: any) {
    this.communityForm?.get('team')?.setValue(team);
  }

  displayTeamName(team?: any): string {
    return team ? team.name : '';
  }

  searchUser(query: string) {
    if (query.length > 0) {
      this.searchResults = this.teamsList.filter((team) =>
        team.name.toLowerCase().includes(query.toLowerCase())
      );
    } else {
      this.searchResults = this.teamsList;
    }
  }

  blockUser() {
    if (this.data.blockUserCallback) {
      this.data.blockUserCallback(this.data.user);
    }
    this.dialogRef.close(true);
  }

  flagPost() {
    if (this.data.flagPostCallback) {
      this.data.flagPostCallback(this.data.post);
    }
    this.dialogRef.close(true);
  }

  addStyle() {
    const dynamicColor = this.settingsData?.space_font_primary_color || '#999';
    document.documentElement.style.setProperty('--placeholder-color', dynamicColor);
    document.documentElement.style.setProperty('--invalid-border-color', dynamicColor);
    document.documentElement.style.setProperty('--before-border-color', dynamicColor);

    if (this.settingsData?.font_family) {
      document.documentElement.style.setProperty('--app-font', this.settingsData.font_family);
    }
  }

  ngOnDestroy(): void {
    this.userInfoSubscription?.unsubscribe();
    this.settingsDataSubscription?.unsubscribe();
  }
}
